import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Drawer } from '@/components/ui';
import { Menu, MenuItem, MenuTrigger } from '@/components/ui/menu';
import { env } from '@/config/env';
import { paths } from '@/config/paths';
import Calendar from '@/features/calendar/calendar';
import { useProfilesData } from '@/features/user';
import { NavigationIcon } from '@/layouts/main-layout/components/navigation-icon';
import { FeatureEnum, NAVIGATION_HEADER_ITEMS } from '@/lib/feature-flags';
import useFeature from '@/lib/feature-flags/use-feature';
import { useSchedulePageStore } from '@/stores/schedule-page';

import {
  Module,
  BrainGym,
  Home,
  Calendar as CalendarIcon,
  HobbyMasterClass,
  ModuleReports,
  ParentsCommunity,
  SharpenUp,
} from '../icons';

export type NavigationItem = {
  id: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode;
  label: string;
  path?: string;
  action?: 'navigate' | 'drawer';
};

export const navigationItems: NavigationItem[] = [
  {
    id: NAVIGATION_HEADER_ITEMS.HOME,
    icon: Home,
    label: 'Home',
    path: paths.app.root.getHref(),
    action: 'navigate',
  },
  {
    id: NAVIGATION_HEADER_ITEMS.MODULES,
    icon: Module,
    label: env.IS_OFFLINE ? 'Modules' : 'Recordings',
    path: paths.app.modules.getHref(),
    action: 'navigate',
  },
  {
    id: NAVIGATION_HEADER_ITEMS.BRAIN_GYM,
    icon: BrainGym,
    label: 'Brain Gym',
    path: paths.app.brainGym.getHref(),
    action: 'navigate',
  },
  {
    id: NAVIGATION_HEADER_ITEMS.MODULE_REPORTS,
    icon: ModuleReports,
    label: 'Reports',
    path: paths.app.moduleReports.getHref(),
    action: 'navigate',
  },
  {
    id: NAVIGATION_HEADER_ITEMS.SCHEDULE,
    icon: CalendarIcon,
    label: 'Schedule',
    action: 'drawer',
  },
];

export const expandedNavigationItems: NavigationItem[] = [
  {
    id: 'hobby-master-class',
    icon: HobbyMasterClass,
    label: 'Hobby & Master Class',
    path: paths.app.hobbyMasterClass.getHref(),
    action: 'navigate',
  },
  {
    id: 'parents-community',
    icon: ParentsCommunity,
    label: 'Parents Community',
    path: paths.app.parentsCommunity.getHref(),
    action: 'navigate',
  },
  {
    id: 'sharpen-up',
    icon: SharpenUp,
    label: 'Sharpen-Up',
    path: paths.app.sharpenUp.getHref(),
    action: 'navigate',
  },
];

export interface NavigationProps {
  isFooter?: boolean;
}

const Navigation = ({ isFooter = false }: NavigationProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen: isScheduleDrawerOpen, setIsOpen: setIsScheduleDrawerOpen } =
    useSchedulePageStore();

  const { shouldShowOnboarding } = useProfilesData();

  // Feature flag checks for navigation items
  const showHome = useFeature({
    feature: FeatureEnum.navigationHeader,
    featureItem: NAVIGATION_HEADER_ITEMS.HOME,
  });
  const showModules = useFeature({
    feature: FeatureEnum.navigationHeader,
    featureItem: NAVIGATION_HEADER_ITEMS.MODULES,
  });
  const showBrainGym = useFeature({
    feature: FeatureEnum.navigationHeader,
    featureItem: NAVIGATION_HEADER_ITEMS.BRAIN_GYM,
  });
  const showSchedule = useFeature({
    feature: FeatureEnum.navigationHeader,
    featureItem: NAVIGATION_HEADER_ITEMS.SCHEDULE,
  });

  const showModuleReports = useFeature({
    feature: FeatureEnum.navigationHeader,
    featureItem: NAVIGATION_HEADER_ITEMS.MODULE_REPORTS,
  });

  // Filter navigation items based on feature flags
  const filteredNavigationItems = useMemo(() => {
    const featureMap: Record<string, boolean> = {
      [NAVIGATION_HEADER_ITEMS.HOME]: showHome,
      [NAVIGATION_HEADER_ITEMS.MODULES]: showModules && !shouldShowOnboarding,
      [NAVIGATION_HEADER_ITEMS.BRAIN_GYM]:
        showBrainGym && !shouldShowOnboarding,
      [NAVIGATION_HEADER_ITEMS.MODULE_REPORTS]:
        showModuleReports && !shouldShowOnboarding,
      [NAVIGATION_HEADER_ITEMS.SCHEDULE]: showSchedule && !shouldShowOnboarding,
    };

    return navigationItems.filter(item => featureMap[item.id] ?? true);
  }, [
    showHome,
    showModules,
    showBrainGym,
    showSchedule,
    showModuleReports,
    shouldShowOnboarding,
  ]);

  const handleTabClick = async (item: NavigationItem) => {
    if (
      item.action === 'drawer' &&
      item.id === NAVIGATION_HEADER_ITEMS.SCHEDULE
    ) {
      setIsScheduleDrawerOpen(true);
    } else if (item.path) {
      await navigate(item.path);
    }
  };

  const isMoreActive = () => {
    return expandedNavigationItems.some(item => pathname === item.path);
  };

  // Create the More dropdown menu
  const moreMenu = (
    <Menu className="bg-magical-moonlight-s">
      {expandedNavigationItems.map(item => (
        <MenuItem
          key={item.id}
          onClick={() => void handleTabClick(item)}
          className="flex items-center gap-3 px-4 py-3"
        >
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <>
      <nav className="flex items-center w-full h-full space-x-2 sm:space-x-4 md:space-x-6 lg:space-x-8 flex-1 sm:flex-none justify-around">
        {filteredNavigationItems.map(item => {
          if (item.id === 'more') {
            return (
              <MenuTrigger
                key={item.id}
                menu={moreMenu}
                align={isFooter ? 'end' : 'start'}
                side={isFooter ? 'top' : 'bottom'}
                className="h-full justify-center flex"
              >
                <button className="focus:outline-none flex items-center justify-center h-full">
                  <NavigationIcon
                    icon={item.icon}
                    text={item.label}
                    active={isMoreActive()}
                  />
                </button>
              </MenuTrigger>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => void handleTabClick(item)}
              className="focus:outline-none flex items-center justify-center h-full"
            >
              <NavigationIcon
                icon={item.icon}
                text={item.label}
                active={
                  item.action === 'drawer' &&
                  item.id === NAVIGATION_HEADER_ITEMS.SCHEDULE
                    ? isScheduleDrawerOpen
                    : pathname === item.path
                }
              />
            </button>
          );
        })}
      </nav>

      {/* Schedule Drawer */}
      <Drawer
        isOpen={isScheduleDrawerOpen}
        onClose={() => setIsScheduleDrawerOpen(false)}
        contentClassName="bg-white"
      >
        <Calendar />
      </Drawer>
    </>
  );
};

export { Navigation };
